import React from 'react';
import {useTranslation} from 'react-i18next';
import {ShieldAlert} from 'lucide-react';
import type {
    StandingConsequence,
    StandingLevel,
    UserRestrictionDTO
} from './AccountStanding';

interface StandingConsequencesProps {
    data: UserRestrictionDTO;
    getStandingColor: (level: StandingLevel) => string;
    getStandingBackground: (level: StandingLevel) => string;
    getPenaltyByLevel: (level: StandingLevel) => string;
}

const StandingConsequences = ({
                                  data,
                                  getStandingColor,
                                  getStandingBackground,
                                  getPenaltyByLevel
                              }: StandingConsequencesProps) => {
    const {t} = useTranslation();

    const standingConsequences: StandingConsequence[] = [
        {
            level: 'EXCELLENT',
            penalty: '0%',
            consequence: getPenaltyByLevel('EXCELLENT')
        },
        {
            level: 'GOOD',
            penalty: '6%',
            consequence: getPenaltyByLevel('GOOD')
        },
        {
            level: 'POOR',
            penalty: '10%',
            consequence: getPenaltyByLevel('POOR')
        },
        {
            level: 'AT_RISK',
            penalty: '14%',
            consequence: getPenaltyByLevel('AT_RISK')
        },
        {
            level: 'BANNED',
            penalty: '100%',
            consequence: getPenaltyByLevel('BANNED')
        }
    ];

    const getPointRange = (level: StandingLevel) => {
        switch (level) {
            case 'EXCELLENT':
                return '0';
            case 'GOOD':
                return '0–100';
            case 'POOR':
                return '100–200';
            case 'AT_RISK':
                return '200–300';
            case 'BANNED':
                return '300+';
            default:
                return '—';
        }
    };

    return (
        <div className="mt-4 rounded-xl border border-white/5 bg-white/5 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                <ShieldAlert size={15} className="text-orange-500"/>

                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    {t('dashboard.profile.standing.consequences')}
                </span>
            </div>

            <div className="hidden md:grid grid-cols-[1fr_0.8fr_1fr_1.5fr] gap-4 px-4 py-2 border-b border-white/5">
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                    {t('dashboard.profile.standing.title')}
                </span>

                <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                    {t('dashboard.profile.standing.active_points')}
                </span>

                <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                    {t('dashboard.profile.standing.cancellation_penalty')}
                </span>

                <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                    {t('dashboard.profile.standing.consequence')}
                </span>
            </div>

            <div className="divide-y divide-white/5">
                {standingConsequences.map(consequence => {
                    const isCurrent = consequence.level === data.standing;

                    return (
                        <div
                            key={consequence.level}
                            className={`grid grid-cols-1 md:grid-cols-[1fr_0.8fr_1fr_1.5fr] gap-2 md:gap-4 px-4 py-3 transition-colors ${
                                isCurrent
                                    ? getStandingBackground(consequence.level)
                                    : 'hover:bg-white/[0.02]'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-[10px] font-black uppercase tracking-wider ${getStandingColor(consequence.level)}`}>
                                    {t(`dashboard.profile.standing.${consequence.level.toLowerCase()}`)}
                                </span>

                                {isCurrent && (
                                    <span
                                        className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${getStandingBackground(consequence.level)} ${getStandingColor(consequence.level)}`}>
                                        {t('dashboard.profile.standing.current')}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center">
                                <span className="text-xs font-black font-mono">
                                    {getPointRange(consequence.level)}
                                </span>
                            </div>

                            <div className="flex items-center">
                                <span className="text-xs font-black font-mono">
                                    {consequence.penalty}
                                </span>
                            </div>

                            <div className="flex items-center">
                                <span className="text-[10px] font-bold text-gray-500 dark:text-slate-400">
                                    {consequence.consequence}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default StandingConsequences;